import { useCallback, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";

import type { MobileRuntimeConfig } from "../../config/runtime-config.js";
import { synthesizePatientWorkspaceSpeech } from "../patient-workspace-client.js";
import type { ChatMessageViewModel } from "../types.js";
import { resolvePatientBrand } from "./brand.js";

const brand = resolvePatientBrand();

export type PatientWorkspaceReadAloudControls = {
  activeReadAloudMessageId: string | null;
  isAutoReadAloudEnabled: boolean;
  isReadingAloud: boolean;
  readMessageAloud: (message: ChatMessageViewModel | null) => void;
  toggleAutoReadAloud: () => void;
};

export type PatientWorkspaceVoiceInputControls = {
  isListening: boolean;
  isSupported: boolean;
  toggleListening: () => void;
};

type PatientSpeechRecognitionAlternativeWindow = Window & {
  SpeechRecognition?: new () => PatientSpeechRecognitionInstance;
  webkitSpeechRecognition?: new () => PatientSpeechRecognitionInstance;
  mozSpeechRecognition?: new () => PatientSpeechRecognitionInstance;
  msSpeechRecognition?: new () => PatientSpeechRecognitionInstance;
};

type PatientSpeechRecognitionResult = {
  [index: number]: { transcript?: string } | undefined;
  isFinal?: boolean;
  length: number;
};

type PatientSpeechRecognitionResultEvent = {
  resultIndex: number;
  results: ArrayLike<PatientSpeechRecognitionResult>;
};

type PatientSpeechRecognitionErrorEvent = {
  error?: string;
};

type PatientSpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  abort: () => void;
  start: () => void;
  stop: () => void;
  onend: (() => void) | null;
  onerror: ((event: PatientSpeechRecognitionErrorEvent) => void) | null;
  onresult: ((event: PatientSpeechRecognitionResultEvent) => void) | null;
  onstart: (() => void) | null;
};

const patientWorkspaceAutoReadAloudStorageKey =
  "amplecare_patient_workspace_auto_read_aloud_enabled";
const patientWorkspaceVoiceAutoSendDelayMs = 2_800;
function resolvePatientSpeechSynthesis(): SpeechSynthesis | null {
  if (
    Platform.OS !== "web" ||
    typeof window === "undefined" ||
    typeof window.speechSynthesis === "undefined" ||
    typeof SpeechSynthesisUtterance === "undefined"
  ) {
    return null;
  }

  return window.speechSynthesis;
}

function resolvePatientSpeechRecognitionConstructor():
  | (new () => PatientSpeechRecognitionInstance)
  | null {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return null;
  }

  const speechWindow = window as PatientSpeechRecognitionAlternativeWindow;

  return (
    speechWindow.SpeechRecognition ??
    speechWindow.webkitSpeechRecognition ??
    speechWindow.mozSpeechRecognition ??
    speechWindow.msSpeechRecognition ??
    null
  );
}

function resolvePatientPreferredSpeechVoice(
  synthesis: SpeechSynthesis,
): SpeechSynthesisVoice | null {
  const voices = synthesis.getVoices();

  if (voices.length === 0) {
    return null;
  }

  const preferredNamePatterns = [
    /samantha/i,
    /victoria/i,
    /ava/i,
    /susan/i,
    /zira/i,
    /jenny/i,
    /aria/i,
    /karen/i,
    /moira/i,
    /tessa/i,
    /fiona/i,
    /allison/i,
    /martha/i,
    /marta/i,
    /michelle/i,
    /google us english/i,
  ];
  const avoidedNamePatterns = [
    /alex/i,
    /daniel/i,
    /david/i,
    /fred/i,
    /george/i,
    /mark/i,
    /paul/i,
    /ralph/i,
    /tom/i,
    /male/i,
  ];

  return [...voices]
    .map((voice) => {
      const nameScore = preferredNamePatterns.reduce(
        (score, pattern, index) =>
          pattern.test(voice.name) ? Math.max(score, 120 - index) : score,
        0,
      );
      const avoidedScore = avoidedNamePatterns.some((pattern) =>
        pattern.test(voice.name),
      )
        ? -100
        : 0;
      const localeScore =
        voice.lang === "en-US" ? 40 : voice.lang.startsWith("en") ? 20 : 0;

      return {
        score: nameScore + avoidedScore + localeScore,
        voice,
      };
    })
    .sort((left, right) => right.score - left.score)[0]?.voice ?? null;
}

function readStoredPatientAutoReadAloud(): boolean {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return false;
  }

  return (
    window.localStorage.getItem(patientWorkspaceAutoReadAloudStorageKey) ===
    "true"
  );
}

function writeStoredPatientAutoReadAloud(value: boolean): void {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    patientWorkspaceAutoReadAloudStorageKey,
    value ? "true" : "false",
  );
}

export function findLatestReadablePatientAgentMessage(
  messages: readonly ChatMessageViewModel[],
): ChatMessageViewModel | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (
      message &&
      message.senderKind !== "patient" &&
      message.senderKind !== "system" &&
      message.bodyText.trim().length > 0
    ) {
      return message;
    }
  }

  return null;
}

function formatPatientVoiceTranscript(transcript: string): string {
  const cleanedTranscript = transcript.replace(/\s+/g, " ").trim();

  if (!cleanedTranscript) {
    return "";
  }

  return `${cleanedTranscript.charAt(0).toUpperCase()}${cleanedTranscript.slice(1)}`;
}

export function usePatientWorkspaceReadAloud(input: {
  conversationMessages: readonly ChatMessageViewModel[];
  patientId: string | null;
  runtimeConfig: MobileRuntimeConfig;
  onNoticeChange: (value: string | null) => void;
}): PatientWorkspaceReadAloudControls {
  const { conversationMessages, onNoticeChange, patientId, runtimeConfig } =
    input;
  const latestReadableMessage =
    findLatestReadablePatientAgentMessage(conversationMessages);
  const [isAutoReadAloudEnabled, setIsAutoReadAloudEnabled] = useState(
    readStoredPatientAutoReadAloud,
  );
  const [activeReadAloudMessageId, setActiveReadAloudMessageId] = useState<
    string | null
  >(null);
  const [isReadingAloud, setIsReadingAloud] = useState(false);
  const utteranceTokenRef = useRef(0);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const audioObjectUrlRef = useRef<string | null>(null);
  const speechAbortControllerRef = useRef<AbortController | null>(null);
  const lastAutoReadMessageIdRef = useRef<string | null>(
    latestReadableMessage?.id ?? null,
  );

  const stopActiveAudioPlayback = useCallback(() => {
    speechAbortControllerRef.current?.abort();
    speechAbortControllerRef.current = null;

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.removeAttribute("src");
      audioPlayerRef.current.load();
      audioPlayerRef.current = null;
    }

    if (audioObjectUrlRef.current && typeof URL !== "undefined") {
      URL.revokeObjectURL(audioObjectUrlRef.current);
      audioObjectUrlRef.current = null;
    }
  }, []);

  const clearReadAloudState = useCallback(
    (token: number) => {
      if (token !== utteranceTokenRef.current) {
        return;
      }

      stopActiveAudioPlayback();
      setActiveReadAloudMessageId(null);
      setIsReadingAloud(false);
    },
    [stopActiveAudioPlayback],
  );

  const readMessageWithBrowserSpeech = useCallback(
    (
      message: ChatMessageViewModel,
      token: number,
      noticeText: string | null = null,
    ): boolean => {
      const synthesis = resolvePatientSpeechSynthesis();

      if (!synthesis) {
        return false;
      }

      const utterance = new SpeechSynthesisUtterance(message.bodyText);
      const preferredVoice = resolvePatientPreferredSpeechVoice(synthesis);

      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      utterance.lang = preferredVoice?.lang ?? "en-US";
      utterance.rate = 0.96;
      utterance.pitch = preferredVoice ? 1.05 : 1.12;
      utterance.onend = () => clearReadAloudState(token);
      utterance.onerror = () => {
        clearReadAloudState(token);
        onNoticeChange("Read aloud could not play this message.");
      };

      setActiveReadAloudMessageId(message.id);
      setIsReadingAloud(true);
      synthesis.speak(utterance);
      onNoticeChange(
        noticeText ?? `Reading ${brand.assistantName} reply from ${message.timestampText}.`,
      );

      return true;
    },
    [clearReadAloudState, onNoticeChange],
  );

  const readMessageAloud = useCallback(
    (message: ChatMessageViewModel | null) => {
      if (!message) {
        onNoticeChange(`No ${brand.assistantName} reply is available to read aloud yet.`);
        return;
      }

      if (activeReadAloudMessageId === message.id && isReadingAloud) {
        utteranceTokenRef.current += 1;
        resolvePatientSpeechSynthesis()?.cancel();
        stopActiveAudioPlayback();
        setActiveReadAloudMessageId(null);
        setIsReadingAloud(false);
        onNoticeChange("Read aloud stopped.");
        return;
      }

      utteranceTokenRef.current += 1;
      resolvePatientSpeechSynthesis()?.cancel();
      stopActiveAudioPlayback();

      const token = utteranceTokenRef.current;
      setActiveReadAloudMessageId(message.id);
      setIsReadingAloud(true);

      if (
        Platform.OS !== "web" ||
        typeof Audio === "undefined" ||
        typeof URL === "undefined"
      ) {
        if (!readMessageWithBrowserSpeech(message, token)) {
          clearReadAloudState(token);
          onNoticeChange("Read aloud is not supported in this browser.");
        }

        return;
      }

      onNoticeChange(
        `Preparing ${brand.assistantName} reply from ${message.timestampText}.`,
      );

      const abortController = new AbortController();
      speechAbortControllerRef.current = abortController;

      void synthesizePatientWorkspaceSpeech(
        runtimeConfig,
        {
          bodyText: message.bodyText,
          patientId,
        },
        abortController.signal,
      )
        .then(({ audioBlob, voiceId }) => {
          if (token !== utteranceTokenRef.current) {
            return;
          }

          speechAbortControllerRef.current = null;

          const audioObjectUrl = URL.createObjectURL(audioBlob);
          const audioPlayer = new Audio(audioObjectUrl);
          audioObjectUrlRef.current = audioObjectUrl;
          audioPlayerRef.current = audioPlayer;
          audioPlayer.onended = () => clearReadAloudState(token);
          audioPlayer.onerror = () => {
            if (token !== utteranceTokenRef.current) {
              return;
            }

            stopActiveAudioPlayback();

            if (
              !readMessageWithBrowserSpeech(
                message,
                token,
                "Natural voice could not play, so browser read aloud is on.",
              )
            ) {
              clearReadAloudState(token);
              onNoticeChange("Read aloud could not play this message.");
            }
          };

          void audioPlayer.play().then(
            () => {
              if (token === utteranceTokenRef.current) {
                onNoticeChange(
                  `Reading ${brand.assistantName} reply with ${voiceId ?? "AmpleCare Voice"}.`,
                );
              }
            },
            () => {
              if (token !== utteranceTokenRef.current) {
                return;
              }

              stopActiveAudioPlayback();

              if (
                !readMessageWithBrowserSpeech(
                  message,
                  token,
                  "Natural voice could not start, so browser read aloud is on.",
                )
              ) {
                clearReadAloudState(token);
                onNoticeChange("Read aloud could not play this message.");
              }
            },
          );
        })
        .catch((error: unknown) => {
          if (token !== utteranceTokenRef.current) {
            return;
          }

          speechAbortControllerRef.current = null;

          if (
            typeof DOMException !== "undefined" &&
            error instanceof DOMException &&
            error.name === "AbortError"
          ) {
            return;
          }

          if (
            !readMessageWithBrowserSpeech(
              message,
              token,
              "Natural voice is unavailable, so browser read aloud is on.",
            )
          ) {
            clearReadAloudState(token);
            onNoticeChange("Read aloud could not play this message.");
          }
        });
    },
    [
      activeReadAloudMessageId,
      clearReadAloudState,
      isReadingAloud,
      onNoticeChange,
      patientId,
      readMessageWithBrowserSpeech,
      runtimeConfig,
      stopActiveAudioPlayback,
    ],
  );

  const toggleAutoReadAloud = useCallback(() => {
    setIsAutoReadAloudEnabled((currentValue) => {
      const nextValue = !currentValue;

      writeStoredPatientAutoReadAloud(nextValue);

      if (nextValue) {
        lastAutoReadMessageIdRef.current = latestReadableMessage?.id ?? null;
        onNoticeChange(`Read aloud is on for new ${brand.assistantName} replies.`);
      } else {
        onNoticeChange("Read aloud is off.");
      }

      return nextValue;
    });
  }, [latestReadableMessage?.id, onNoticeChange]);

  useEffect(() => {
    const synthesis = resolvePatientSpeechSynthesis();

    synthesis?.getVoices();

    return () => {
      utteranceTokenRef.current += 1;
      synthesis?.cancel();
      stopActiveAudioPlayback();
    };
  }, [stopActiveAudioPlayback]);

  useEffect(() => {
    if (!latestReadableMessage) {
      lastAutoReadMessageIdRef.current = null;
      return;
    }

    if (!isAutoReadAloudEnabled) {
      lastAutoReadMessageIdRef.current = latestReadableMessage.id;
      return;
    }

    if (lastAutoReadMessageIdRef.current == null) {
      lastAutoReadMessageIdRef.current = latestReadableMessage.id;
      return;
    }

    if (lastAutoReadMessageIdRef.current === latestReadableMessage.id) {
      return;
    }

    lastAutoReadMessageIdRef.current = latestReadableMessage.id;
    readMessageAloud(latestReadableMessage);
  }, [isAutoReadAloudEnabled, latestReadableMessage, readMessageAloud]);

  return {
    activeReadAloudMessageId,
    isAutoReadAloudEnabled,
    isReadingAloud,
    readMessageAloud,
    toggleAutoReadAloud,
  };
}

export function usePatientWorkspaceVoiceInput(input: {
  onAutoSendTranscript?: (value: string) => Promise<boolean> | boolean | void;
  onNoticeChange: (value: string | null) => void;
  onTranscript: (value: string) => void;
}): PatientWorkspaceVoiceInputControls {
  const { onAutoSendTranscript, onNoticeChange, onTranscript } = input;
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<PatientSpeechRecognitionInstance | null>(null);
  const latestTranscriptRef = useRef("");
  const onAutoSendTranscriptRef =
    useRef<typeof onAutoSendTranscript>(onAutoSendTranscript);
  const autoSendTimerRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);
  const SpeechRecognitionConstructor =
    resolvePatientSpeechRecognitionConstructor();
  const isSupported = SpeechRecognitionConstructor != null;

  useEffect(() => {
    onAutoSendTranscriptRef.current = onAutoSendTranscript;
  }, [onAutoSendTranscript]);

  const clearAutoSendTimer = useCallback(() => {
    if (autoSendTimerRef.current == null) {
      return;
    }

    globalThis.clearTimeout(autoSendTimerRef.current);
    autoSendTimerRef.current = null;
  }, []);

  const scheduleVoiceAutoSend = useCallback(() => {
    const transcript = latestTranscriptRef.current.trim();

    clearAutoSendTimer();

    if (!transcript || !onAutoSendTranscriptRef.current) {
      return;
    }

    autoSendTimerRef.current = globalThis.setTimeout(() => {
      const nextTranscript = latestTranscriptRef.current.trim();

      if (!nextTranscript || !onAutoSendTranscriptRef.current) {
        return;
      }

      onNoticeChange(`Sending voice message to ${brand.assistantName}.`);

      void Promise.resolve(onAutoSendTranscriptRef.current(nextTranscript))
        .then((wasApplied) => {
          if (wasApplied === false) {
            return;
          }

          latestTranscriptRef.current = "";
        })
        .catch(() => {
          onNoticeChange(
            "Voice message could not be sent. Please review it and press SEND.",
          );
        });
    }, patientWorkspaceVoiceAutoSendDelayMs);
  }, [clearAutoSendTimer, onNoticeChange]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionConstructor) {
      onNoticeChange("Voice input is not supported in this browser.");
      return;
    }

    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognitionConstructor();
    }

    clearAutoSendTimer();
    latestTranscriptRef.current = "";

    const recognition = recognitionRef.current;

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onstart = () => {
      setIsListening(true);
      onNoticeChange(
        "Listening. Speak your message, then pause briefly to send.",
      );
    };
    recognition.onend = () => {
      setIsListening(false);
      scheduleVoiceAutoSend();
    };
    recognition.onerror = (event) => {
      setIsListening(false);
      clearAutoSendTimer();
      onNoticeChange(
        event.error
          ? `Voice input stopped: ${event.error}.`
          : "Voice input stopped before a message was captured.",
      );
    };
    recognition.onresult = (event) => {
      const transcriptParts: string[] = [];
      let includesFinalResult = false;

      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript;

        if (transcript) {
          transcriptParts.push(transcript);
        }

        if (result?.isFinal) {
          includesFinalResult = true;
        }
      }

      const transcript = formatPatientVoiceTranscript(
        transcriptParts.join(" "),
      );

      if (transcript) {
        latestTranscriptRef.current = transcript;
        onTranscript(transcript);

        if (includesFinalResult) {
          scheduleVoiceAutoSend();
        }
      }
    };

    try {
      recognition.start();
    } catch {
      onNoticeChange("Voice input is already listening.");
    }
  }, [
    SpeechRecognitionConstructor,
    clearAutoSendTimer,
    onNoticeChange,
    onTranscript,
    scheduleVoiceAutoSend,
  ]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
      onNoticeChange("Voice input stopped.");
      return;
    }

    startListening();
  }, [isListening, onNoticeChange, startListening, stopListening]);

  useEffect(() => {
    return () => {
      clearAutoSendTimer();
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, [clearAutoSendTimer]);

  return {
    isListening,
    isSupported,
    toggleListening,
  };
}
